/**
 * Form validation for merchant onboarding
 * Validates business profile, representative, bank details, and documents
 */

import { RootState } from "@funded-here-interface/investor-onboarding/src/store";
import { LegalStructure } from "../../../investor-onboarding/src/constants/enums";
import {
  validateRequired,
  validateEmail,
  validateUrl,
  validatePhoneNumber,
  validateFileUpload,
  validateConditional,
  validateEquals,
  validateNotFutureDate,
  addError,
  cleanupEmptyErrors,
} from "./validators";

export interface FormErrors {
  businessProfile?: { [key: string]: string };
  representative?: { [key: string]: string };
  bankDetails?: { [key: string]: string };
  documents?: { [key: string]: string };
  [key: string]: { [key: string]: string } | undefined;
}

/**
 * Validate business profile section
 */
const validateBusinessProfile = (borBiz: any): { [key: string]: string } => {
  const errors: { [key: string]: string } = {};

  // Required fields
  const requiredFields = [
    { field: borBiz.company, name: "companyName", label: "Company name" },
    {
      field: borBiz.regNumber,
      name: "registrationNumber",
      label: "Registration number",
    },
    {
      field: borBiz.incorporationDate,
      name: "incorporationDate",
      label: "Incorporation Date",
    },
    {
      field: borBiz.legalStructure,
      name: "legalStructure",
      label: "Legal structure",
    },
    {
      field: borBiz.industryGroup,
      name: "industryGroup",
      label: "Industry group",
    },
    {
      field: borBiz.typeOfInstitute,
      name: "institutionType",
      label: "Industry",
    },
    {
      field: borBiz.countryOfIncorp,
      name: "countryOfIncorporation",
      label: "Country of incorporation",
    },
    {
      field: borBiz.countryOfDomicile,
      name: "countryOfDomicile",
      label: "Country of domicile",
    },
    {
      field: borBiz.website,
      name: "companyWebsite",
      label: "Company Website",
    },
    { field: borBiz.address, name: "address", label: "Address" },
    { field: borBiz.postal, name: "postalcode", label: "Postal code" },
  ];

  requiredFields.forEach(({ field, name, label }) => {
    const result = validateRequired(field, label);
    if (!result.isValid) addError(errors, name, result.error!);
  });

  // Conditional: Legal structure "Others" requires specification
  const legalStructureOthersResult = validateConditional(
    borBiz.legalStructure === LegalStructure.OTHERS,
    borBiz.legalStructureOthers,
    "Legal structure specification"
  );
  if (!legalStructureOthersResult.isValid) {
    addError(errors, "legalStructureOthers", legalStructureOthersResult.error!);
  }

  // Optional website with format validation
  if (borBiz.website) {
    const urlResult = validateUrl(borBiz.website);
    if (!urlResult.isValid)
      addError(errors, "companyWebsite", urlResult.error!);
  }

  // Phone number validation
  const phoneResult = validatePhoneNumber(borBiz.phoneNumber, "Phone number");
  if (!phoneResult.isValid) addError(errors, "phoneNumber", phoneResult.error!);

  // Date of Incorporation cannot be a future date
  const dateResult = validateNotFutureDate(
    borBiz.incorporationDate,
    "Date of Incorporation"
  );
  if (!dateResult.isValid)
    addError(errors, "incorporationDate", dateResult.error!);

  return errors;
};

/**
 * Validate representative section
 */
const validateRepresentative = (
  Borrep: any,
  state: RootState
): { [key: string]: string } => {
  const errors: { [key: string]: string } = {};

  // Required fields for merchant user
  const merchantFields = [
    {
      field: Borrep.merchantFirstName,
      name: "merchantFirstName",
      label: "First name",
    },
    {
      field: Borrep.merchantLastName,
      name: "merchantLastName",
      label: "Last name",
    },
    { field: Borrep.merchantTitle, name: "merchantTitle", label: "Role" },
  ];

  merchantFields.forEach(({ field, name, label }) => {
    const result = validateRequired(field, label);
    if (!result.isValid) addError(errors, name, result.error!);
  });

  // Required fields for authorized representative
  const repFields = [
    {
      field: Borrep.repFirstName,
      name: "repFirstName",
      label: "Representative's first name",
    },
    {
      field: Borrep.repLastName,
      name: "repLastName",
      label: "Representative's last name",
    },
    {
      field: Borrep.repTitle,
      name: "repTitle",
      label: "Representative's role",
    },
  ];

  repFields.forEach(({ field, name, label }) => {
    const result = validateRequired(field, label);
    if (!result.isValid) addError(errors, name, result.error!);
  });

  // Representative email validation
  const emailResult = validateEmail(Borrep.repEmail, "Representative's email");
  if (!emailResult.isValid) addError(errors, "repEmail", emailResult.error!);

  // Validate shareholders/directors
  const expectedDirectors = Borrep.numOfShareholdersDirectors || 0;
  if (expectedDirectors < 1) {
    addError(
      errors,
      "shareholdersDirectors",
      "At least one director is required"
    );
  } else {
    const directors = Borrep.shareholdersDirectors || [];

    // Validate each director
    for (let index = 0; index < expectedDirectors; index++) {
      const director = directors[index] || {};
      const prefix = `Director ${index + 1}`;

      if (!director.firstName) {
        addError(
          errors,
          `shareholdersDirectors.${index}.firstName`,
          `${prefix} first name is required`
        );
      }
      if (!director.lastName) {
        addError(
          errors,
          `shareholdersDirectors.${index}.lastName`,
          `${prefix} last name is required`
        );
      }
      if (!director.role) {
        addError(
          errors,
          `shareholdersDirectors.${index}.role`,
          `${prefix} role is required`
        );
      }

      // Email validation
      if (!director.email) {
        addError(
          errors,
          `shareholdersDirectors.${index}.email`,
          `${prefix} email is required`
        );
      } else {
        const directorEmailResult = validateEmail(
          director.email,
          `${prefix} email`
        );
        if (!directorEmailResult.isValid) {
          addError(
            errors,
            `shareholdersDirectors.${index}.email`,
            `${prefix} invalid email format`
          );
        }

        // Check for duplicate emails
        const duplicateEmails = directors.filter(
          (d: any, i: number) =>
            i !== index && d.email?.trim() !== "" && d.email === director.email
        );
        if (duplicateEmails.length > 0) {
          addError(
            errors,
            `shareholdersDirectors.${index}.email`,
            `${prefix} email must be unique`
          );
        }
      }
    }

    // Max directors validation
    if (directors.length > 50) {
      addError(errors, "shareholdersDirectors", "Maximum 50 directors allowed");
    }
  }

  // Representative email consistency checks
  if (
    state.auth.userEmail &&
    Borrep.repEmail === state.auth.userEmail &&
    !Borrep.authChecked
  ) {
    addError(
      errors,
      "repEmail",
      "Representative's email cannot match your email unless 'Will you be acting as an Authorized Representative?' is checked"
    );
  }

  if (
    Borrep.shareholdersDirectors &&
    Borrep.repEmail &&
    !Borrep.directorChecked
  ) {
    const isDirectorEmail = Borrep.shareholdersDirectors.some(
      (d: any) => d.email === Borrep.repEmail
    );
    if (isDirectorEmail) {
      addError(
        errors,
        "repEmail",
        "Representative's email cannot match a director's email unless 'Is the authorized representative one of the above directors?' is checked"
      );
    }
  }

  return errors;
};

/**
 * Validate bank details section
 */
const validateBankDetails = (
  BorBank: any,
  companyName: string | null
): { [key: string]: string } => {
  const errors: { [key: string]: string } = {};

  // Required fields
  const requiredFields = [
    { field: BorBank.bankName, name: "bankName", label: "Bank name" },
    {
      field: BorBank.accountNumber,
      name: "accountNumber",
      label: "Account number",
    },
    { field: BorBank.bankAddress, name: "bankAddress", label: "Bank address" },
    { field: BorBank.bankCountry, name: "bankCountry", label: "Bank country" },
    { field: BorBank.currency, name: "currency", label: "Currency" },
    { field: BorBank.accountType, name: "accountType", label: "Account Type" },
    { field: BorBank.swiftCode, name: "swiftCode", label: "SWIFT code" },
  ];

  requiredFields.forEach(({ field, name, label }) => {
    const result = validateRequired(field, label);
    if (!result.isValid) addError(errors, name, result.error!);
  });

  // Account holder name validation with company name match
  const holderNameResult = validateRequired(
    BorBank.accountHolderName,
    "Account holder name"
  );
  if (!holderNameResult.isValid) {
    addError(errors, "accountHolderName", holderNameResult.error!);
  } else {
    const equalsResult = validateEquals(
      BorBank.accountHolderName,
      companyName,
      "Account holder name",
      "company name"
    );
    if (!equalsResult.isValid) {
      addError(errors, "accountHolderName", equalsResult.error!);
    }
  }

  return errors;
};

/**
 * Validate documents section
 */
const validateDocuments = (bordoc: any): { [key: string]: string } => {
  const errors: { [key: string]: string } = {};

  // File upload validations
  const fileFields = [
    {
      files: bordoc.business,
      name: "businessProfileFile",
      label: "Company/Business Profile",
    },
    {
      files: bordoc.certificateOfIncorporation,
      name: "certificateOfIncorporation",
      label: "Certificate Of Incorporation",
    },
    {
      files: bordoc.supportingDocumentsWithSuppliers,
      name: "supportingDocumentsWithSuppliers",
      label: "Supporting Documents With Suppliers",
    },
    {
      files: bordoc.bank,
      name: "bankStatement",
      label: "Proof of Bank Account Details",
    },
    {
      files: bordoc.proof,
      name: "proofOfAddress",
      label: "Proof of Residential Address",
    },
    {
      files: bordoc.balance,
      name: "balanceSheet",
      label: "Financial Documents",
    },
  ];

  fileFields.forEach(({ files, name, label }) => {
    const result = validateFileUpload(files, label);
    if (!result.isValid) addError(errors, name, result.error!);
  });

  return errors;
};

/**
 * Main validation function for merchant onboarding form
 */
export const validateForm = (state: RootState): FormErrors => {
  const errors: FormErrors = {};
  const { borBiz, Borrep, BorBank, bordoc } = state;

  // Validate each section
  errors.businessProfile = validateBusinessProfile(borBiz);
  errors.representative = validateRepresentative(Borrep, state);
  errors.bankDetails = validateBankDetails(BorBank, borBiz.company);
  errors.documents = validateDocuments(bordoc);

  // Clean up empty error sections
  cleanupEmptyErrors(errors);

  return errors;
};
